// src/services/chatService.js
// UPDATED: calls /api/chat/message on your Spring Boot backend instead of
// calling Anthropic directly from the browser (fixes CORS preflight 400 error).
// The Anthropic API key stays on the server — never exposed to the browser.

import { executeToolCall } from './toolRouter';

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

// ── Build clinical context string for system prompt injection ─────────────────
// Summarises the patient's latest triage result and care plan into a compact
// string that ChatService injects into the governed system prompt (v1.1).
// Only patient-readable fields included — no raw UUIDs or internal metadata.
// Sprint 13 (18 Jun 2026): enables Ask Previu to answer questions about the
// patient's own test results.
export function buildTriageContext(welcomeSummary) {
  if (!welcomeSummary) return null;

  const { lastResult, openActionItems, totalActionItems, previuAge, previuAgeDelta } = welcomeSummary;
  const lines = [];

  if (lastResult) {
    const riskLevel = lastResult.consensusRiskLevel || 'UNKNOWN';
    const riskScore = lastResult.consensusRiskScore != null ? lastResult.consensusRiskScore : null;
    const drawDate  = lastResult.drawDate
      ? new Date(lastResult.drawDate).toLocaleDateString('en-IN',
          { day: 'numeric', month: 'short', year: 'numeric' })
      : 'recent draw';

    lines.push(`Latest blood test (${drawDate}):`);
    lines.push(`- Risk level: ${riskLevel}${riskScore != null ? `, Risk score: ${riskScore}/100` : ''}`);

    if (lastResult.claudeKeyFindings) {
      // Strip [AUTO] internal prefix in case it leaked
      const findings = lastResult.claudeKeyFindings
        .replace(/^\[AUTO\][\s\S]*?\|\s*/g, '').trim();
      if (findings) lines.push(`- Key findings: ${findings.slice(0, 400)}`);
    }

    if (lastResult.claudeRecommendation) {
      const rec = lastResult.claudeRecommendation
        .replace(/^\[AUTO\][\s\S]*?\|\s*/g, '').trim();
      if (rec) lines.push(`- Recommendations: ${rec.slice(0, 300)}`);
    }

    if (lastResult.hasAcuteAlert && lastResult.acuteAlertDetail) {
      lines.push(`- ⚠ ACUTE ALERT: ${lastResult.acuteAlertDetail}`);
    }
  } else {
    lines.push('No blood test results on file yet for this patient.');
  }

  if (previuAge) {
    const sign  = previuAgeDelta != null && parseFloat(previuAgeDelta) >= 0 ? '+' : '';
    const delta = previuAgeDelta != null ? ` (${sign}${previuAgeDelta} yrs vs chronological age)` : '';
    lines.push(`Previu biological age: ${previuAge} yrs${delta}`);
  }

  if (totalActionItems > 0) {
    lines.push(`Care plan: ${openActionItems} open item${openActionItems !== 1 ? 's' : ''} of ${totalActionItems} total`);
  }

  return lines.length > 0 ? lines.join('\n') : null;
}

/**
 * Main entry point — sends messages to the Spring Boot chat proxy.
 * The proxy handles Anthropic streaming and tool_use detection server-side,
 * forwarding SSE text deltas back to the browser.
 * Tool calls (Previu API calls) are still executed client-side via toolRouter.js.
 *
 * @param {object[]} messages  Anthropic-format conversation history
 * @param {object}   patient   Patient identity from localStorage. Only `patient.id` is sent: the server
 *                   rebuilds name/age/gender from the database (ChatController.trustedPatient), so the
 *                   browser no longer transmits them.
 * @param {function} onChunk   Called with each streamed text delta string
 * @param {function} onToolUse Called when a tool call starts { toolName, input }
 * @param {function} onToolResult Called when a tool call returns { toolName, input, result }
 *                   — input is included so the caller can tell WHICH action of a
 *                   multi-action tool finished (toolRouter.js dispatches on input.action)
 * @returns {Promise<{fullText:string, finalMessages:object[], truncated:boolean}>}
 *          truncated: the last reply hit the model's token limit and stops mid-sentence
 */
export async function sendChatMessage({ messages, patient, triageContext, onChunk, onToolUse, onToolResult }) {
  const jwt = localStorage.getItem('previu_token');

  // Prune catalog from incoming history before first send —
  // if the patient already picked a test, we don't need the full list anymore
  let conversationMessages = pruneCatalogFromHistory([...messages]);
  let fullText = '';
  // True when the LAST model turn ended on stop_reason "max_tokens": the reply
  // the patient sees stops mid-sentence. Last turn wins — a tool turn that was
  // cut short is followed by a fresh answer turn.
  let truncated = false;
  let symptomSaveNudged = false; // 9 Jul 2026 — see nudge logic below
  let syntheticMessageIndex = -1; // index of the nudge message, if one gets added — never sent to Anthropic tainted with extra fields, just tracked separately

  // Max 8 tool-call iterations per user message (increased from 5 — multi-marker
  // show_marker_trend requests need headroom for parallel tool calls + final response).
  for (let i = 0; i < 8; i++) {
    const response = await fetch(`${API_BASE}/chat/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      },
      body: JSON.stringify({
        messages: conversationMessages,
        patient: { id: patient?.id },
        // Clinical context for system prompt injection — enables answers about patient's own results
        ...(triageContext ? { triageContext } : {}),
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      // 403 means JWT expired or session invalid — redirect to login
      if (response.status === 403 || response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('patient');
        window.location.href = '/login?reason=session_expired';
        return { fullText: '', finalMessages: [] };
      }
      throw new Error(err?.message ?? `Chat API error ${response.status}`);
    }

    // Parse the SSE stream from the backend
    const { text, toolUseBlocks, stopReason, hitMaxTokens } = await parseStream(response.body, onChunk);
    fullText += text;
    truncated = hitMaxTokens;

    // 15 Jul 2026: log every turn's tool outcome — added while diagnosing
    // "download latest triage report" getting a generic "I can't do that"
    // reply. Root cause found: TOOL_INTENT_KEYWORDS (backend, ChatService.java)
    // had no matching keyword, so tools were never attached to the request at
    // all for that message — this log line makes that class of bug visible
    // from the browser console without needing a backend log dive.
    console.log('[chatService] turn result:', {
      stopReason,
      toolUseBlockNames: toolUseBlocks.map(tb => tb.name),
      textPreview: text?.slice(0, 120),
    });

    // Build assistant message for next turn
    const assistantContent = [];
    if (text) assistantContent.push({ type: 'text', text });
    for (const tb of toolUseBlocks) assistantContent.push(tb);
    conversationMessages.push({ role: 'assistant', content: assistantContent });

    // No tool calls — done, UNLESS this looks like the specific failure
    // pattern where Claude narrated a save without actually calling the
    // tool (9 Jul 2026 — confirmed via network tab: stop_reason "end_turn"
    // with text like "let me save all your responses now" and zero
    // tool_use blocks). Detected here rather than relying purely on prompt
    // wording, since that alone was confirmed NOT reliable — Claude kept
    // narrating the save intent without the accompanying tool call even
    // after the addendum was strengthened to say this explicitly.
    if (stopReason !== 'tool_use' || toolUseBlocks.length === 0) {
      // 9 Jul 2026: broadened after confirming the narrower version missed
      // real phrasings already seen in testing — "save all of this for your
      // care team" and "save what we have" both failed the original
      // /save (all )?(your )?(responses|answers)/i pattern. This is now a
      // defense-in-depth safety net, not the primary fix — the actual root
      // cause (tools never attached to the request for symptom-intake
      // conversations at all) was fixed server-side in ChatService.java.
      // Kept here in case Claude occasionally narrates without calling the
      // tool even when it IS available, which can still happen with LLMs.
      const looksLikeUnsavedSymptomIntake = /\bsav(e|ing)\b[^.!?]{0,50}\b(respons|answer|this|what we have|these|your (information|data|progress))/i.test(text);
      if (looksLikeUnsavedSymptomIntake && !symptomSaveNudged) {
        conversationMessages.push({
          role: 'user',
          content: 'Please go ahead and actually call the submit_symptom_answers tool now — ' +
            'saying you will save it isn\'t enough, the tool call itself is what records it.',
        });
        syntheticMessageIndex = conversationMessages.length - 1;
        // Guard against nudging more than once per user turn, in case Claude
        // narrates a save intent a second time without calling the tool again.
        symptomSaveNudged = true;
        continue;
      }
      break;
    }

    // Execute tool calls against Previu APIs
    const toolResults = [];
    for (const toolBlock of toolUseBlocks) {
      if (onToolUse) onToolUse({ toolName: toolBlock.name, input: toolBlock.input });

      let result;
      try {
        result = await executeToolCall(toolBlock.name, toolBlock.input, jwt);
      } catch (err) {
        result = { error: err.message ?? 'Tool call failed' };
      }

      const resultContent = trimToolResult(toolBlock.name, toolBlock.input, result);
      toolResults.push({
        type:        'tool_result',
        tool_use_id: toolBlock.id,
        content:     resultContent,
      });

      // Notify caller with raw result for widget injection (e.g. marker_trend,
      // triage report card). For these two: pass the RAW result — widget needs
      // the full patient/triageResult/triageHistory objects; Claude only needs
      // the trimmed summary (see trimToolResult()'s case for each tool).
      if (onToolResult) {
        try {
          const forWidget = (toolBlock.name === 'show_marker_trend' || toolBlock.name === 'download_triage_report'
              || (toolBlock.name === 'order_lab_test' && toolBlock.input?.action === 'create_order')
              || (toolBlock.name === 'book_appointment' && toolBlock.input?.action === 'create_appointment'))
            ? result                           // raw — full data for widget
            : JSON.parse(resultContent);       // trimmed — all other tools
          onToolResult({ toolName: toolBlock.name, input: toolBlock.input, result: forWidget });
        } catch (e) {
          console.warn('[chatService] onToolResult failed for tool',
            toolBlock.name, e.message);
        }
      }
    }

    conversationMessages.push({ role: 'user', content: toolResults });

    // Break early if all tool results are errors — no point looping
    const allErrors = toolResults.every(r => {
      try { return JSON.parse(r.content)?.error || JSON.parse(r.content)?.status >= 400; }
      catch { return false; }
    });
    if (allErrors) break;

    // After adding new tool results, prune any now-redundant catalog results
    // from earlier in the conversation to keep token count low
    conversationMessages = pruneCatalogFromHistory(conversationMessages);
    conversationMessages = pruneOldToolResults(conversationMessages);
  }

  // Strip the synthetic nudge message (if one was added) before returning —
  // it was needed in conversationMessages so the API round-trip could see
  // it, but the patient never typed it and should never see it rendered as
  // if they did. Filtered by index rather than tainting the message object
  // itself, since conversationMessages gets serialized straight into the
  // next Anthropic API request body — an extra unrecognized field on a
  // message object risks breaking that request's schema validation.
  const uiFacingMessages = syntheticMessageIndex === -1
    ? conversationMessages
    : conversationMessages.filter((_, idx) => idx !== syntheticMessageIndex);
  return { fullText, finalMessages: uiFacingMessages, truncated };
}

/**
 * Parses the SSE stream from the Spring Boot proxy.
 * The proxy forwards Anthropic SSE events with the same shape:
 *   data: {"type":"text_delta","text":"..."}
 *   data: {"type":"tool_use_start","name":"..."}
 *   data: [DONE]
 */
/**
 * Strips tool_result content from messages older than the last 6 turns.
 * Prevents context bloat in long ordering flows where repeated slot searches
 * and profile fetches accumulate thousands of tokens.
 * Claude loses state when the context is truncated by the API silently.
 */
function pruneOldToolResults(messages) {
  if (messages.length <= 12) return messages; // short conversations — no pruning needed
  // Keep the last 12 messages intact; for older messages, replace tool_result content
  // with a summary placeholder so Claude knows a tool was called but doesn't re-process
  return messages.map((msg, idx) => {
    if (idx >= messages.length - 12) return msg; // keep recent messages intact
    if (msg.role === 'user' && Array.isArray(msg.content)) {
      const hasToolResult = msg.content.some(c => c.type === 'tool_result');
      if (!hasToolResult) return msg;
      return {
        ...msg,
        content: msg.content.map(c => {
          if (c.type !== 'tool_result') return c;
          return { ...c, content: '[result available — see conversation context]' };
        }),
      };
    }
    return msg;
  });
}

/**
 * Prunes catalog tool_results from conversation history once a test has been
 * selected. Detected by the presence of a search_slots or create_order tool_use
 * anywhere later in the history — meaning the patient already picked a test.
 *
 * Replaces the full catalog result with a one-line summary to save tokens
 * while keeping the conversation structurally valid for Anthropic.
 */
function pruneCatalogFromHistory(messages) {
  // Check if a slot search or order has happened — means catalog was already used
  const patientSelectedTest = messages.some(msg => {
    if (msg.role !== 'assistant') return false;
    const content = Array.isArray(msg.content) ? msg.content : [];
    return content.some(block =>
      block.type === 'tool_use' &&
      block.name === 'order_lab_test' &&
      (block.input?.action === 'search_slots' || block.input?.action === 'create_order')
    );
  });

  if (!patientSelectedTest) return messages; // catalog still needed

  // Replace catalog tool_result content with a compact summary
  return messages.map(msg => {
    if (msg.role !== 'user') return msg;
    const content = Array.isArray(msg.content) ? msg.content : [];
    const hasCatalog = content.some(block => {
      if (block.type !== 'tool_result') return false;
      try {
        const parsed = JSON.parse(block.content);
        return parsed?.skuList !== undefined;
      } catch { return false; }
    });

    if (!hasCatalog) return msg;

    // Replace catalog content with minimal summary
    return {
      ...msg,
      content: content.map(block => {
        if (block.type !== 'tool_result') return block;
        try {
          const parsed = JSON.parse(block.content);
          if (parsed?.skuList === undefined) return block;
          return {
            ...block,
            content: JSON.stringify({
              note: 'Catalog already shown to patient — test selected, proceeding with order.',
              totalTests: parsed.skuList?.length ?? 0,
            }),
          };
        } catch { return block; }
      }),
    };
  });
}

/**
 * Trims tool results before storing in conversation history.
 * The Thyrocare catalog response can be 8,000+ tokens — we only need
 * a summary for Claude to work with, not every test's full details.
 */
function trimToolResult(toolName, input, result) {
  if (!result || typeof result !== 'object') return JSON.stringify(result);

  // 15 Jul 2026: create_order no longer calls Thyrocare directly — it
  // returns a payment card result (LabTestPaymentCard.jsx via
  // lab_test_payment_card). Claude only needs a short confirmation; the
  // full orderPayload exists purely for the card's click handler (see
  // forWidget pass-through below) and would blow past the 3000-char
  // generic truncation fallback otherwise, risking invalid JSON sent to Claude.
  if (toolName === 'order_lab_test' && input?.action === 'create_order') {
    if (result?.type !== 'lab_test_payment_card') {
      return JSON.stringify(result); // error/awaiting_confirmation cases — pass through as-is
    }
    return JSON.stringify({
      status: 'payment_card_shown',
      panelName: result.panelName,
      note: 'A payment card was shown to the patient in chat — nothing has been ordered yet. ' +
        'The Thyrocare order is only created after the patient completes payment by clicking the ' +
        'card. Respond with ONLY a short sentence pointing at the card, e.g. "Tap the card below to ' +
        'pay and confirm your ' + (result.panelName || 'test') + ' booking." Do NOT say the order ' +
        '"has been placed" or confirm booking details as if it\'s already done.',
    });
  }

  // 16 Jul 2026: same pattern as create_order above, for doctor appointments —
  // create_appointment no longer calls /appointments directly, it returns a
  // payment card result (AppointmentPaymentCard.jsx via appointment_payment_card).
  if (toolName === 'book_appointment' && input?.action === 'create_appointment') {
    if (result?.type !== 'appointment_payment_card') {
      return JSON.stringify(result); // error/awaiting_confirmation cases — pass through as-is
    }
    return JSON.stringify({
      status: 'payment_card_shown',
      doctorName: result.doctorName,
      note: 'A payment card was shown to the patient in chat — nothing has been booked yet. ' +
        'The appointment is only created after the patient completes payment by clicking the ' +
        'card. Respond with ONLY a short sentence pointing at the card, e.g. "Tap the card below to ' +
        'pay and confirm your appointment' + (result.doctorName ? ' with ' + result.doctorName : '') +
        '." Do NOT say the appointment "has been booked" or confirm details as if it\'s already done.',
    });
  }

  // Trim catalog list — keep only id, name, price, fasting flag per item
  if (toolName === 'order_lab_test' && input?.action === 'list_catalog') {
    const skuList = result.skuList ?? result.data ?? [];
    const trimmed = skuList.slice(0, 20).map(sku => ({
      id:    sku.id,
      type:  sku.type,   // MUST be passed to order — Thyrocare requires the exact type from catalog
      name:  sku.name,
      tests: sku.noOfTestsIncluded,
      price: sku.rate?.sellingPrice ?? sku.rate?.listingPrice,
      fasting: sku.flags?.isFastingRequired,
      homeCollectible: sku.flags?.isHomeCollectible,
    }));
    return JSON.stringify({
      isLastPage: result.isLastPage,
      nextPage:   result.nextPage,
      totalShown: trimmed.length,
      skuList:    trimmed,
    });
  }

  // search_slots: group by hour and send representative slots so Claude can present
  // real options. Sending only 3 caused hallucination — Claude told patients
  // "only 5 AM available" when 7 AM, 8 AM etc. were in the full response.
  // Strategy: keep the first slot of each hour band (morning/afternoon/evening)
  // so Claude has a compact but complete picture to present. Cap at 15 slots max.
  if (toolName === 'order_lab_test' && input?.action === 'search_slots') {
    const slots = result.slots ?? result.availableSlots ?? result.data ?? result ?? [];
    const slotArr = Array.isArray(slots) ? slots : [];

    // Deduplicate by hour — keep first slot per hour to reduce token usage
    // while ensuring Claude knows every hour band that has availability.
    const seenHours = new Set();
    const deduped = slotArr.filter(s => {
      const hour = (s.startTime ?? '').slice(0, 2); // '07' from '07:00'
      if (seenHours.has(hour)) return false;
      seenHours.add(hour);
      return true;
    }).slice(0, 15); // hard cap at 15 for token safety

    const trimmed = deduped.map(s => ({
      startTime: s.startTime ?? s.time,
      endTime:   s.endTime,
    }));

    return JSON.stringify({
      timeZone:       result.timeZone ?? 'IST',
      appointmentDate: result.appointmentDate,
      availableSlots: trimmed,
      totalSlots:     slotArr.length,
      note: 'List ALL startTime values above for the patient to choose from. Do NOT show only one slot.',
    });
  }

  // get_panel_pricing: trim to essentials — but MUST keep skuId, skuType, skuName
  // as Claude needs these for create_order items[]. Dropping them causes Claude
  // to fabricate item ids/names which Thyrocare rejects.
  if (toolName === 'get_panel_pricing') {
    const panels = result.panels ?? [];
    const trimmed = panels.map(p => ({
      code:      p.code,
      name:      p.name,
      price:     p.price,
      fasting:   p.fasting,
      skuId:     p.skuId,    // REQUIRED — use as items[].id in create_order
      skuType:   p.skuType,  // REQUIRED — use as items[].type in create_order
      skuName:   p.skuName,  // REQUIRED — use as items[].name in create_order
    }));
    return JSON.stringify({
      type:            result.type,
      requestedPanels: result.requestedPanels,
      panels:          trimmed,
      total:           result.total,
      availableAddOns: (result.availableAddOns ?? []).map(a => ({
        name: a.name, price: a.price, category: a.category
      })),
    });
  }

  // show_marker_trend: send Claude a compact summary only — NOT the full dataPoints array.
  // The widget gets full data via onToolResult (separate path). Sending hundreds of KB
  // of dataPoints to Claude causes the continuation message to time out.
  if (toolName === 'show_marker_trend') {
    try {
      const r = typeof result === 'string' ? JSON.parse(result) : result;
      if (r.type === 'marker_trend_multi') {
        // Summarise each marker — count + latest value only
        const summary = {};
        for (const [name, data] of Object.entries(r.results || {})) {
          const pts = data.dataPoints || [];
          const latest = pts[pts.length - 1];
          summary[name] = {
            drawCount: pts.length,
            latestValue: latest ? `${latest.value} ${data.unit}` : 'no data',
            status: latest?.status || '',
          };
        }
        return JSON.stringify({
          type: 'marker_trend_multi',
          markerNames: r.markerNames,
          summary,
          note: 'Chart rendered for patient. Summarise the trends factually from the summary above.',
        });
      }
      if (r.type === 'marker_trend') {
        const pts = r.dataPoints || [];
        return JSON.stringify({
          type: 'marker_trend',
          markerName: r.markerName,
          unit: r.unit,
          drawCount: pts.length,
          dataPoints: pts, // single marker — small enough to keep
          note: r.note || undefined,
          availableMarkers: r.availableMarkers || undefined,
        });
      }
    } catch { /* fall through to raw stringify */ }
    return JSON.stringify(result);
  }

  // download_triage_report: Claude only needs a short confirmation — the
  // full patient/triageResult/triageHistory objects exist purely for
  // TriageReportCard to call downloadPatientReport() on click (see
  // toolRouter.js). Sending the full objects to Claude would be wasted
  // tokens and unnecessary PII duplication in the model context.
  if (toolName === 'download_triage_report') {
    if (result?.type !== 'triage_report_card') {
      return JSON.stringify(result); // error case — pass through as-is
    }
    return JSON.stringify({
      status: 'report_card_shown',
      reportId: result.reportId,
      drawDate: result.drawDate,
      riskLevel: result.riskLevel,
      note: 'A clickable report card was shown to the patient in chat — nothing has opened yet, ' +
        'the card itself opens the download when THEY click it. Respond with ONLY a short sentence ' +
        'like: "Here\'s your latest report from ' + (result.drawDate || 'your last draw') + ' — tap the ' +
        'card below to download it as a PDF." Do NOT say the report "has been opened", do NOT mention ' +
        'pop-up blockers (the card shows that error itself if it happens), and do NOT re-describe the ' +
        'clinical findings.',
    });
  }

  // For all other results, stringify as-is but cap at 3000 chars
  const str = JSON.stringify(result);
  if (str.length > 3000) {
    return str.slice(0, 3000) + '... [truncated]';
  }
  return str;
}

async function parseStream(body, onChunk) {
  const reader  = body.getReader();
  const decoder = new TextDecoder();
  let buffer    = '';
  let text      = '';
  const toolUseBlocks   = [];
  let stopReason        = 'end_turn';
  let hitMaxTokens      = false;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop(); // keep incomplete line

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const raw = line.slice(6).trim();
      if (raw === '[DONE]') continue;

      let event;
      try { event = JSON.parse(raw); } catch { continue; }

      switch (event.type) {
        case 'text_delta':
          text += event.text;
          if (onChunk) onChunk(event.text);
          break;

        case 'tool_use':
          // Backend sends complete tool_use block
          toolUseBlocks.push({
            type:  'tool_use',
            id:    event.id    ?? `tool_${Date.now()}`,
            name:  event.name  ?? '',
            input: event.input ?? {},
          });
          stopReason = 'tool_use';
          break;

        case 'stop':
          // Backend forwards Anthropic's stop_reason. stopReason above is left
          // alone (the tool loop depends on it); max_tokens is tracked apart.
          if (event.stop_reason === 'max_tokens') hitMaxTokens = true;
          break;

        case 'error':
          throw new Error(event.message ?? 'Stream error');

        default:
          break;
      }
    }
  }

  return { text, toolUseBlocks, stopReason, hitMaxTokens };
}
